import { ChangeDetectorRef, Directive } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { InspectionModel } from '@main-application/inspections/models/inspection.model';
import { InspectionService } from '@main-application/inspections/services/inspection.service';
import { RoutePath } from '@shared/enums/route-path.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

import { InspectionStatus } from '../models/rest-inspections-model.interface';

@Directive()
export class InspectionsBaseComponent extends ComponentAbstract {
  readonly InspectionStatus = InspectionStatus;

  constructor(
    protected cdr: ChangeDetectorRef,
    protected inspectionService: InspectionService,
    protected snackbarService: SnackbarService,
    protected modalService: ModalsService,
    public router: Router
  ) {
    super(cdr);
  }

  share(inspection: InspectionModel, evt?: Event) {
    if (evt) {
      evt.preventDefault();
    }

    if (inspection.entityDetails.status === InspectionStatus.Completed) {
      this.inspectionService.openSendDialog(
        inspection.entityDetails.id,
        `${inspection.inspectionType} inspection: ${inspection.entityDetails.unit.propertyName} - ${inspection.entityDetails.unit.name}`
      );
    } else {
      this.snackbarService.error(`You can only share a completed inspection`);
    }
  }

  protected tableSortPredicate = (inspection: InspectionModel, sortHeaderId: string) => {
    switch (sortHeaderId) {
      case 'unit.name':
        return inspection.entityDetails.unit.name?.toLocaleLowerCase();
      case 'turboMode':
        return inspection.entityDetails.isTurboMode;
      case 'unit.propertyName':
        return inspection.entityDetails.unit.propertyName?.toLocaleLowerCase();
      case 'inspectionType':
        return (inspection.inspectionTemplate?.name ?? inspection.inspectionType)?.toLocaleLowerCase();
      case 'rooms':
        return inspection.areasLabel;
      case 'assignee':
        return inspection.entityDetails.assigneeUser?.displayName?.toLocaleLowerCase();
      case 'createdAt':
        return inspection.entityDetails.createdAt;
      case 'completedAt':
        return inspection.entityDetails.completedAt;
      case 'dueDate':
        return inspection.entityDetails.dueDate;
      default:
        return inspection[sortHeaderId];
    }
  };

  download(inspection: InspectionModel, evt?: Event) {
    if (evt) {
      evt.preventDefault();
    }

    if (inspection.entityDetails.status === InspectionStatus.Completed) {
      if (inspection.entityDetails.attachments?.attachment?.url) {
        window.open(inspection.entityDetails.attachments.attachment.url);
      } else {
        this.snackbarService.error('Inspection .pdf not found');
      }
    } else {
      this.snackbarService.error('Inpspection must be completed first');
    }
  }

  delete(inspection: InspectionModel) {
    return this.modalService
      .openConfirmationModal(
        {
          title: `Delete inspection?`,
          content: `Delete inspection for ${inspection.propertyName} - ${inspection.unitName}?`,
          confirmColor: 'warn',
          confirmLabel: 'Delete',
        },
        'xs'
      )
      .afterClosed()
      .pipe(
        filter(result => result === DialogResult.Success),
        mergeMap(() => {
          return this.inspectionService.delete(inspection.id).pipe(
            catchError((_, caught) => {
              this.snackbarService.error('Error deleting inspection');
              return caught;
            })
          );
        })
      );
  }

  open(inspection: InspectionModel, evt?: Event) {
    if (evt) {
      evt.preventDefault();
    }

    if (inspection.entityDetails.status === InspectionStatus.Completed) {
      this.router.navigate([RoutePath.INSPECTIONS, inspection.entityDetails.id]);
    } else {
      this.snackbarService.error('Only completed inspections can be viewed');
    }
  }
}
